import React from 'react';
import { MessageCircle, Share2, Users } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WhatsApp Share Button Component
 * Allows users to share session details via WhatsApp to recruit friends
 */
export default function WhatsAppShareButton({ session, variant = 'default', showText = true }) {
  const { t, lang } = useTranslation();
  if (!session) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const generateShareMessage = () => {
    const spotsLeft = session.max_participants - session.current_participants;
    const sessionUrl = `${window.location.origin}/session/${session.session_id}`;
    
    const lines = [
      `\u{1F3C3} *${session.title}*`,
      '',
      `\u{1F4C5} ${formatDate(session.date)} - ${session.start_time}`,
      `\u{1F4CD} ${session.location}`,
      `\u{1F4B0} ${session.price}\u20AC`,
      `\u23F1\uFE0F ${session.duration_minutes} ${t('sessionDetail.minutes')}`,
      '',
    ];
    
    if (spotsLeft > 0) {
      lines.push(`\u{1F465} *${t('whatsapp.msgSpotsLeft', { count: spotsLeft })}*`, '');
    }
    
    lines.push(`${t('whatsapp.msgBookSpot')} \u{1F447}`, sessionUrl);
    
    return lines.join('\n');
  };

  const handleShare = () => {
    const message = generateShareMessage();
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'compact') {
    return (
      <button onClick={handleShare} className="flex items-center gap-1 text-green-500 hover:text-green-400 transition-colors p-2 rounded-lg hover:bg-green-500/10" title={t('whatsapp.shareOnWhatsApp')} data-testid="whatsapp-share-compact">
        <MessageCircle className="w-5 h-5" />
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <Button onClick={handleShare} variant="ghost" size="sm" className="text-green-500 hover:text-green-400 hover:bg-green-500/10" title={t('whatsapp.shareOnWhatsApp')} data-testid="whatsapp-share-icon">
        <MessageCircle className="w-5 h-5" />
      </Button>
    );
  }

  return (
    <Button onClick={handleShare} className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 w-full" data-testid="whatsapp-share-btn">
      <MessageCircle className="w-5 h-5" />
      {showText && <span>{t('whatsapp.inviteFriends')}</span>}
      <Users className="w-4 h-4 ml-1 opacity-70" />
    </Button>
  );
}

/**
 * Share Success Component - Shows after booking
 * Encourages user to invite friends to fill the session
 */
export function WhatsAppShareSuccess({ session, onClose }) {
  const { t, lang } = useTranslation();
  if (!session) return null;

  const spotsLeft = session.max_participants - session.current_participants;
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const generateShareMessage = () => {
    const sessionUrl = `${window.location.origin}/session/${session.session_id}`;
    
    const lines = [
      `\u{1F389} ${t('whatsapp.msgBooked')}`,
      '',
      `\u{1F3C3} *${session.title}*`,
      `\u{1F4C5} ${formatDate(session.date)} - ${session.start_time}`,
      `\u{1F4CD} ${session.location}`,
      `\u{1F4B0} ${session.price}\u20AC`,
      '',
    ];
    
    if (spotsLeft > 0) {
      lines.push(`${t('whatsapp.msgSpotsLeft', { count: spotsLeft })} \u{1F4AA}`, '');
    }
    
    lines.push(`${t('whatsapp.msgBookSpot')} \u{1F447}`, sessionUrl);
    
    return lines.join('\n');
  };

  const handleShare = () => {
    const message = generateShareMessage();
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border border-green-500/30 rounded-xl p-6 animate-fade-in">
      <div className="text-center mb-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
          <Share2 className="w-8 h-8 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-green-400 mb-2">
          {t('whatsapp.bookingConfirmed')}
        </h3>
        <p className="text-muted-foreground">
          {spotsLeft > 0 
            ? t('whatsapp.spotsLeftInvite').replace('{{count}}', spotsLeft)
            : t('whatsapp.shareSession')
          }
        </p>
      </div>
      
      <Button
        onClick={handleShare}
        className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-lg"
        data-testid="whatsapp-share-success-btn"
      >
        <MessageCircle className="w-5 h-5 mr-2" />
        {t('whatsapp.shareOnWhatsApp')}
      </Button>
      
      {onClose && (
        <button
          onClick={onClose}
          className="w-full mt-3 text-muted-foreground hover:text-foreground text-sm"
        >
          {t('whatsapp.maybeLater')}
        </button>
      )}
    </div>
  );
}
